/**
 * Types for the standalone single SEG-Y file viewer (no registered prep run).
 */

/** Highpass / lowpass phase mode applied when filtering a gather. */
export type FilterPhase = "zero" | "min";

/** Render metadata returned alongside a rasterized gather image. */
export interface GatherMeta {
  clip_value: number;
  vmin: number;
  vmax: number;
  n_traces: number;
  ns: number;
  dt: number;
  t_max: number;
  width: number;
  height: number;
  decim_traces: number;
  decim_time: number;
}

export interface GatherResponse {
  /** base64-encoded PNG */
  image: string;
  meta: GatherMeta;
}

export interface RawHeadersResponse {
  /** decoded 3200-byte EBCDIC/ASCII text header */
  text_header: string;
  /** full rev1 binary-header dump, field name -> value */
  binary_header: { [field: string]: number };
  [key: string]: any;
}

export interface RawTraceHeadersResponse {
  total_traces: number;
  offset: number;
  limit: number;
  /** the fields materialized in `rows` (parallel order) */
  columns: string[];
  rows: number[][];
  /** every standard rev1 field name, in byte order (for the column chooser) */
  available_columns?: string[];
  /** field name -> 1-based inclusive [start, end] byte range */
  field_bytes?: { [name: string]: [number, number] };
  /** the default subset shown when no explicit selection is requested */
  default_columns?: string[];
}

export interface RawGatherParams {
  ffid?: number;
  trace_start?: number;
  trace_count?: number;
  clip?: number;
  min_val?: number;
  max_val?: number;
  lowcut?: number;
  low_roll?: number;
  highcut?: number;
  high_roll?: number;
}

/** Header-derived metadata of one raw SEG-Y file (from /api/v1/segy/info/). */
export interface SegyFileInfo {
  path: string;
  is_aws: boolean;
  size: number;
  ns: number;
  dt: number | null;
  format: number;
  n_traces: number | null;
}

/** A SEG-Y file entry returned by the directory lookup. */
export interface SegyDirFile {
  name: string;
  size: number;
}

/** Result of /api/v1/segy/listdir/: the sub-folders and SEG-Y files of a
 * directory, for finding a raw input without typing its full path. */
export interface SegyDirListing {
  path: string;
  is_aws: boolean;
  dirs: string[];
  files: SegyDirFile[];
}

/** Trace-header fields a gather can be grouped by (matches the backend
 * `GROUPABLE_FIELDS`); FFID first as the common default. */
export const SEGY_GROUP_FIELDS = [
  "ffid",
  "source",
  "chan",
  "soux",
  "souy",
  "recx",
  "recy",
  "recelev",
  "souelev",
  "soudepth",
] as const;

/** Gather/spectrum addressing + filter params for /api/v1/segy/{gather,spectrum}/. */
export interface SegyGatherParams {
  /** group by an arbitrary trace-header field (e.g. "source") */
  scan_field?: string;
  scan_value?: number;
  /** shorthand for scan_field=ffid */
  ffid?: number;
  trace_start?: number;
  trace_count?: number;
  clip?: number;
  lowcut?: number;
  /** highpass rolloff slope in dB/octave */
  low_roll?: number;
  /** highpass phase mode (zero-phase vs minimum-phase) */
  low_phase?: FilterPhase;
  highcut?: number;
  /** lowpass rolloff slope in dB/octave */
  high_roll?: number;
  /** lowpass phase mode (zero-phase vs minimum-phase) */
  high_phase?: FilterPhase;
}

/** A user-defined trace-header field read at an arbitrary 1-based byte with a
 * chosen numeric format — for plotting / inspecting non-standard headers. */
export interface CustomHeaderField {
  /** 1-based byte position within the 240-byte trace header */
  byte: number;
  /** one of {@link SEGY_HEADER_FORMATS} (int16/uint16/int32/uint32/...) */
  format: string;
}

/** Numeric formats accepted for a custom header field (mirror the backend
 * HEADER_FIELD_FORMATS); label is what the picker shows. */
export const SEGY_HEADER_FORMATS: { value: string; label: string }[] = [
  { value: "int32", label: "int32 (4-byte signed)" },
  { value: "uint32", label: "uint32 (4-byte unsigned)" },
  { value: "int16", label: "int16 (2-byte signed)" },
  { value: "uint16", label: "uint16 (2-byte unsigned)" },
  { value: "int8", label: "int8 (1-byte signed)" },
  { value: "uint8", label: "uint8 (1-byte unsigned)" },
  { value: "ieee", label: "IEEE float (4-byte)" },
];

/** Backend field name for a custom field (matches segy_dtypes.custom_field_name);
 * the client builds it to find the field in a scan / trace-header response. */
export function customHeaderFieldName(f: CustomHeaderField): string {
  return `b${f.byte}_${f.format}`;
}

/** Request token for a custom field (`byte:format`), as the API expects. */
export function customHeaderFieldToken(f: CustomHeaderField): string {
  return `${f.byte}:${f.format}`;
}

/** Source / receiver positions scanned from the trace headers. */
export interface SegyGeometryResponse {
  sources: { x: number[]; y: number[] };
  receivers: { x: number[]; y: number[] };
  n_traces: number;
  /** how many traces were actually scanned (≤ n_traces when sampled) */
  scanned_traces?: number;
  n_sources: number;
  n_receivers: number;
  /** true when only a strided sample of traces was scanned (large files) */
  sampled?: boolean;
  decimated: boolean;
}

/** Average amplitude spectrum of the addressed gather (raw + filtered). */
export interface SegySpectrumResponse {
  freq: number[];
  raw: number[];
  filtered: number[] | null;
  df: number;
  ns: number;
  file_dt?: number | null;
}

/** Per-trace values of every groupable header field (+ any custom byte/format
 * fields), for cross-plotting. */
export interface SegyHeaderPlotResponse {
  /** 1-based trace numbers (a strided sample on large files) */
  trace: number[];
  fields: { [name: string]: number[] };
  columns: string[];
  /** the subset of `columns` that came from custom byte/format specs */
  custom_columns?: string[];
  n_traces: number;
  /** how many traces were actually scanned (≤ n_traces when sampled) */
  scanned_traces?: number;
  /** true when only a strided sample of traces was scanned (large files) */
  sampled?: boolean;
}

/** The S3 buckets available for browsing + the custom-field formats the API
 * accepts (from /api/v1/segy/buckets/). */
export interface SegyBucketsResponse {
  buckets: string[];
  formats: string[];
}

/** Full rev1 header of one trace (+ any custom fields) for the trace-click
 * readout (from /api/v1/segy/trace_header/). */
export interface SegyTraceHeaderResponse {
  /** 1-based trace number */
  trace: number;
  /** 0-based absolute trace index */
  trace_index: number;
  /** every standard rev1 field, name -> value */
  fields: { [name: string]: number };
  /** field name -> 1-based inclusive [start, end] byte range */
  field_bytes: { [name: string]: [number, number] };
  /** custom byte/format fields, name -> value */
  custom: { [name: string]: number };
  /** custom field name -> 1-based inclusive [start, end] byte range */
  custom_bytes: { [name: string]: [number, number] };
}

/** Affine (inline/crossline) or principal-axis grid fit of a model volume. */
export interface SegyModelGrid {
  method: string;
  /** rotation / azimuth in degrees clockwise from north */
  rotation: number;
  /** affine fit only ↓ */
  origin?: [number, number];
  corners?: [number, number][];
  bin_inline?: number;
  bin_crossline?: number;
  inline_azimuth?: number;
  crossline_azimuth?: number;
  inline_range?: [number, number];
  crossline_range?: [number, number];
  inline_increment?: number;
  crossline_increment?: number;
  n_inlines?: number;
  n_crosslines?: number;
  /** principal-axis fallback only ↓ */
  major_axis_length?: number;
  minor_axis_length?: number;
}

/** Survey geometry of a 3D SEG-Y model / volume (from /api/v1/segy/model_geometry/). */
export interface SegyModelGeometryResponse {
  path: string;
  n_traces: number;
  scanned_traces: number;
  sampled: boolean;
  /** which header carried the coordinates ("cdp …" or "source …") */
  coordinate_source: string;
  has_coordinates: boolean;
  has_inline_crossline: boolean;
  bbox: {
    min_x: number;
    max_x: number;
    min_y: number;
    max_y: number;
    width: number;
    height: number;
  } | null;
  grid: SegyModelGrid | null;
  z_axis: { n_samples: number; interval: number | null; total: number | null };
  format: number;
}
